import { useState } from 'react';
import type { PrepareInterimDownloadResult } from '../lib/api-client.js';
import { usePrepareInterimDownload } from '../lib/queries.js';

/** The `available` arm of PrepareInterimDownload: the URL + outstanding counts. */
export type InterimDownloadAvailable = Extract<
  PrepareInterimDownloadResult['result'],
  { available: unknown }
>['available'];

/** The `notAvailable` arm: wrong stage / no completed cases (F-149 FR-011). */
export type InterimDownloadNotAvailable = Extract<
  PrepareInterimDownloadResult['result'],
  { notAvailable: unknown }
>['notAvailable'];

/**
 * F-149 (US1) interim (draft) export trigger, split like {@link
 * useDownloadTestScript} so the "Download current progress" button can live in
 * the topbar while the outstanding-work banner (US2) / empty hint (US3) render
 * in the Assembly body. Runs PrepareInterimDownload; an `available` result hands
 * the presigned URL to the browser download AND is exposed for the body's
 * non-blocking warning. Never finalises (FR-004).
 */
export function useInterimExport(
  trialId: string,
  onReady?: (presignedUrl: string) => void,
): {
  onDownload: () => void;
  pending: boolean;
  available: InterimDownloadAvailable | null;
  notAvailable: InterimDownloadNotAvailable | null;
} {
  const prepare = usePrepareInterimDownload(trialId);
  const [available, setAvailable] = useState<InterimDownloadAvailable | null>(null);
  const [notAvailable, setNotAvailable] = useState<InterimDownloadNotAvailable | null>(null);
  const onDownload = () => {
    setAvailable(null);
    setNotAvailable(null);
    prepare.mutate(undefined, {
      onSuccess: ({ result }) => {
        if ('available' in result) {
          setAvailable(result.available);
          (onReady ?? triggerBrowserDownload)(result.available.url);
        } else {
          setNotAvailable(result.notAvailable);
        }
      },
    });
  };
  return { onDownload, pending: prepare.isPending, available, notAvailable };
}

/** Default browser-download trigger: a transient anchor click on the presigned URL. */
const triggerBrowserDownload = (presignedUrl: string): void => {
  const link = document.createElement('a');
  link.href = presignedUrl;
  link.rel = 'noopener';
  // No `download` attribute — the presigned URL's Content-Disposition names the file.
  document.body.appendChild(link);
  link.click();
  link.remove();
};

export type InterimDownloadViewProps = {
  /** The latest interim `available` result (URL + counts) — drives the US2 banner. */
  available?: InterimDownloadAvailable | null;
  /** The latest interim `notAvailable` result — drives the US3 empty hint. */
  notAvailable?: InterimDownloadNotAvailable | null;
};

const plural = (count: number, noun: string): string => `${count} ${noun}${count === 1 ? '' : 's'}`;

/**
 * The interim-download flow's presentational body. After a successful interim
 * download it surfaces a NON-blocking outstanding-work banner (US2, FR-005):
 * how many INCLUDED cases are not yet complete and how many are undecided — both
 * omitted from this draft. Reuses the DownloadGate breakdown style; the download
 * is never blocked by it. With nothing outstanding the banner is withheld (US2
 * scenario 3). The US3 empty-case hint lands here too.
 */
export function InterimDownloadView({
  available = null,
  notAvailable = null,
}: InterimDownloadViewProps) {
  const outstanding =
    available !== null && (available.incompleteCount > 0 || available.undecidedCount > 0);
  // FR-011/SC-006: an explanatory hint when there is nothing to export — never an
  // empty file. (The topbar control is also disabled proactively, FR-011.)
  const noCompletedCases = notAvailable?.reason === 'no_completed_cases';
  if (!outstanding && !noCompletedCases) return null;
  return (
    <div data-interim-download-body className="flex flex-col gap-3">
      {noCompletedCases ? (
        <p role="note" className="m-0 text-sm font-semibold text-text-secondary">
          Complete at least one included case to download your progress.
        </p>
      ) : null}
      {outstanding && available !== null ? (
        <section
          role="status"
          aria-label="Outstanding work omitted from this draft"
          className="flex flex-col gap-1 rounded-md border border-[#f7e3a1] bg-[#fff7d6] px-4 py-3 text-md text-ink-800"
        >
          <p className="m-0 font-semibold">This draft contains only the completed work.</p>
          <p className="m-0 text-sm text-text-secondary">
            {plural(available.incompleteCount, 'included case')} not yet complete ·{' '}
            {plural(available.undecidedCount, 'undecided case')} — omitted from this draft.
          </p>
        </section>
      ) : null}
    </div>
  );
}
